import { useLocalStorage } from '@mantine/hooks'
import { atom, useSetAtom } from 'jotai'
import type { RefObject, PropsWithChildren } from 'react'
import { createContext, useRef } from 'react'
import { type NavigateFunction } from 'react-router'
import { match } from 'ts-pattern'
import type {
  BookmarkItem,
  Media,
  Task,
} from 'src/services/apollo/generated/graphql'
import { createCtx, createCtxReducer } from 'src/utils/contextState'
import { ImageGeneratorType } from './constants'
import {
  createFillEditorContextUtilsV2,
  type FillEditorContextUtilsV2,
} from './GenerativeFill/GenerativeFillEditorV2/utils/context'
import type { I2vChannelType } from './ImageToVideo/helper'
import type { GeneratedItemId, TaskOutputBatchItem } from './utils'

/**
 * Generated Task area, which tasks and media are shown, whether they’re deleted / bookmarked
 */
export const [GeneratedItemsContext, GeneratedItemsContextProvider] = createCtx<
  GeneratedItemId[]
>([])
GeneratedItemsContext.displayName = 'GeneratedItemsContext'

/**
 * Generated Task area, set id of which task is selected, set which dialog open
 */
export const [SelectedTaskContext, SelectedTaskContextProvider] = createCtx<{
  taskId?: string | undefined
  /**
   * If task has batch media, which media is selected
   */
  baseMedia?: Media
  /**
   * Which batch media should be published
   */
  mediaIdx?: number
  openPublishDialog?: boolean
  openUpscaleDialog?: boolean
  openVariationDialog?: boolean
  openCaptioningDialog?: boolean
  openDirectEditDialog?: boolean
}>({})
SelectedTaskContext.displayName = 'SelectedTaskContext'

/**
 * Which generated taskId / mediaId is currently hovered
 */
export const [HoveredItemContext, HoveredItemContextProvider] = createCtx<
  string | undefined
>(undefined)
HoveredItemContext.displayName = 'HoveredItemContext'

export const [ImageEditorContext, ImageEditorContextProvider] = createCtx<{
  showEditor: boolean
  isInpaintOn: boolean
  dirty: boolean
}>({
  showEditor: false,
  isInpaintOn: false,
  dirty: false,
})

ImageEditorContext.displayName = 'ImageEditorContext'

/**
 * Last imported task's or artwork's id
 */
export const [LastImportedItemContext, LastImportedItemContextProvider] =
  createCtx<string | undefined>(undefined)

export const [InitialValueLoadedContext, InitialValueLoadedContextProvider] =
  createCtx<boolean>(false)

export const FillEditorContext = createContext<
  RefObject<FillEditorContextUtilsV2>
>({ current: createFillEditorContextUtilsV2() })
FillEditorContext.displayName = 'FillEditorContext'

export const [FillEditorGenParamsContext, FillEditorGenParamsContextProvider] =
  createCtx({
    /**
     * User input prompts for prepending later
     */
    inputPrompts: '',
    /**
     * for inpaint: should be set empty for uploaded image, should be task.parameters.prompts for imported tasks
     * for outpaint: should be initially set from task.parameters.prompts, and append with caption
     */
    originalPrompts: '',
  })

export function imageGeneratorTypeFromParamsTab(
  tab?: string,
): ImageGeneratorType {
  return match(tab)
    .with('editor', () => ImageGeneratorType.Fill)
    .with('enhancement', () => ImageGeneratorType.EnhanceImage)
    .with('animation', () => ImageGeneratorType.ImageToVideo)
    .otherwise(() => ImageGeneratorType.Generate)
}

export function createImageGeneratorTypeUrl(mode: ImageGeneratorType) {
  switch (mode) {
    case ImageGeneratorType.Fill:
      return '/generator/image/editor'
    case ImageGeneratorType.EnhanceImage:
      return '/generator/image/enhancement'
    case ImageGeneratorType.ImageToVideo:
      return '/generator/image/animation'
    default:
      return '/generator/image'
  }
}

/**
 * Types of generator
 *
 * Yes, it is confusing.
 * But this is for the whole purpose of using generator, different from `ImageGeneratorType`
 * which is for the purpose of creating images.
 * We might revise this in the future.
 */
export enum GeneratorType {
  Image = 'image',
  Realtime = 'realtime',
  // Manga = 'manga',  @deprecated at 2025-07-23
  /** @deprecated */
  AnimatedComic = 'animated-comic',
  Upscaler = 'upscaler',
  ImageStitching = 'image-sticthing',
  Mio = 'mio',
  ChatEditing = 'chat-editing',
  ChatEditingPreset = 'preset',
}

// Generator context (reducer style)
export const [GeneratorContext, GeneratorContextProvider] = createCtxReducer(
  (state, action: { type: string; value?: any }) => {
    switch (action.type) {
      case 'set-mode':
        state.navigate?.(createImageGeneratorTypeUrl(action.value))
        return state
      // A hack for separating mode navigation and mode updating to avoid
      // infinite loop
      case 'set-mode-value':
        return { ...state, mode: action.value }
      case 'set-type':
        return { ...state, type: action.value }
    }
    throw new Error(`Unknown action type ${action.type}`)
  },
  {
    navigate: undefined as NavigateFunction | undefined,
    mode: ImageGeneratorType.Generate,
    type: GeneratorType.Image,
  },
)

export const myTasksAtom = atom<Task[]>([])
export const myFavItemsAtom = atom<BookmarkItem[]>([])
export const useSetTaskInMyTasks = () => {
  const setMyTasks = useSetAtom(myTasksAtom)

  function setTask(task: Task) {
    setMyTasks(tasks => {
      const clone = tasks.slice()
      const idx = clone.findIndex(t => t.id === task.id)
      if (idx >= 0) {
        clone.splice(idx, 1, task)
      } else {
        clone.push(task)
      }
      return clone
    })
  }

  function updateTask(taskId: string, taskStateAction: (task: Task) => Task) {
    setMyTasks(tasks => {
      const clone = tasks.slice()
      const idx = clone.findIndex(t => t.id === taskId)
      if (idx >= 0) {
        const task = tasks[idx]

        // HACK: how to update individual task using atom setter?
        const updatedTask = taskStateAction(task)
        clone.splice(idx, 1, updatedTask)
      }
      return clone
    })
  }

  function removeTask(taskId: string) {
    setMyTasks(tasks => {
      const clone = tasks.slice()
      const idx = clone.findIndex(t => t.id === taskId)
      if (idx >= 0) {
        clone.splice(idx, 1)
      }
      return clone
    })
  }

  function updateTaskFavoriteDelete(
    taskId: string,
    opts: {
      mediaId?: string
      type: 'favorite' | 'delete'
      target: boolean
    },
  ) {
    const keyToModify = opts.type === 'favorite' ? 'favoritedAt' : 'deletedAt'

    // Deleting a single task from store
    if (!opts.mediaId && opts.type === 'delete' && opts.target) {
      return removeTask(taskId)
    }
    // Not deleting task itself, but updating its properties
    return updateTask(taskId, task => {
      // Update batch task
      if (opts.mediaId && task.outputs.batch) {
        const batchOutput = [...task.outputs.batch] as TaskOutputBatchItem[]
        const curMediaIdx = batchOutput.findIndex(
          o => o.mediaId === opts.mediaId,
        )
        if (curMediaIdx >= 0) {
          batchOutput[curMediaIdx] = {
            ...batchOutput[curMediaIdx],
            [keyToModify]: opts.target ? new Date().toISOString() : undefined,
          }
          return {
            ...task,
            outputs: {
              ...task.outputs,
              batch: batchOutput,
            },
          }
        }
        return task
      }
      // Favorite single task
      return {
        ...task,
        [keyToModify]: opts.target ? new Date().toISOString() : undefined,
      }
    })
  }
  return {
    setTask,
    updateTask,
    updateTaskFavoriteDelete,
  }
}

/**
 * Using ref to handle various Controls imperatively
 */
export const GeneratorControlsRefContext = createContext<
  RefObject<{
    openControlNet?: () => void
  } | null>
>({
  current: null,
})
export function GeneratorControlsRefContextProvider(props: PropsWithChildren) {
  const ref = useRef({})
  return (
    <GeneratorControlsRefContext.Provider value={ref}>
      {props.children}
    </GeneratorControlsRefContext.Provider>
  )
}

const defaultChannelUpdate: React.Dispatch<
  React.SetStateAction<I2vChannelType>
> = () => 'normal'
export const ChannelContext = createContext<
  [I2vChannelType, React.Dispatch<React.SetStateAction<I2vChannelType>>]
>(['normal', defaultChannelUpdate])
export function ChannelContextProvider(props: PropsWithChildren) {
  const [channel, setChannel] = useLocalStorage<I2vChannelType>({
    key: 'generator::image-to-video-channel',
    defaultValue: 'normal',
    getInitialValueInEffect: false,
  })
  return (
    <ChannelContext.Provider value={[channel, setChannel]}>
      {props.children}
    </ChannelContext.Provider>
  )
}
